import * as z from 'zod';
import client from '../client';

import { Media } from '../strapiSchema';
import { isNotFoundError, logMissingResource, PRIMARY_LOCALE } from '../strapiLocale';

const FooterDropdown = z.object({
  id: z.number(),
  title: z.string(),
  links: z.array(z.object({
    title: z.string().nullish(),
    url: z.string(),
    icon: Media.nullish(),
  })),
});

const Footer = z.object({
  id: z.number(),
  documentId: z.string(),
  locale: z.string(),
  logo: Media,
  contact: z.object({
    title: z.string(),
    email: z.string(),
    phone: z.string().nullish(),
    address: z.string().nullish(),
    hours: z.string(),
  }),
  company: FooterDropdown,
  support: FooterDropdown,
  legal: FooterDropdown,
});

export type FooterType = z.infer<typeof Footer>;

export const getFooter = async (
  locale: string = PRIMARY_LOCALE,
): Promise<FooterType | null> => {
  try {
    const response = await client.get('/api/footer', {
      params: {
        locale,
        populate: [
          'logo',
          'contact',
          'company.links.icon',
          'support.links.icon',
          'legal.links.icon',
        ],
      },
    });

    if (!response.data?.data) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingResource('footer', locale);
        return null;
      }
      throw new Error('No footer found');
    }

    return Footer.parseAsync(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingResource('footer', locale);
      return null;
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match Footer structure : ${errorMessage}`);
  }
};
